import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { getConfig } from '@edx/frontend-platform';
import { AppContext } from '@edx/frontend-platform/react';
import { Icon, Toast } from '@openedx/paragon';
import {
  RiGraduationCapLine, RiBook3Line, RiLockLine, RiRouteLine,
} from '@remixicon/react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchCourseSummary,
  fetchLearnerHomeInit,
} from '@blend-ed/blendxplatform/api';
import { getFeatureToggle } from '@blend-ed/blendxplatform/utils';
import { useIntl } from '@edx/frontend-platform/i18n';

import { useEnrolledPrograms } from '../../gql/hooks/useEnrolledPrograms';
import { useEnrolledPathways } from '../../gql/hooks/useEnrolledPathways';
import { useFetchBanners } from '../../gql';
import { DashboardBanner, selectDashboardBanner } from '../dashboard/components';
import { formatStartDate, daysUntil, computeIsLocked } from './startDateUtils';
import messages from './messages';

const BADGE_TYPE_ICONS = {
  program: RiBook3Line,
  course: RiGraduationCapLine,
  pathway: RiRouteLine,
};

const EnrollmentCard = ({
  title, imageUrl, badgeText, badgeType, homeUrl, to, isLocked, onLockedClick,
}) => {
  const BadgeIcon = BADGE_TYPE_ICONS[badgeType] || RiGraduationCapLine;
  const cardBody = (
    <>
      <div
        className="enrollments-grid-card__image"
        style={{ backgroundImage: `url(${imageUrl})` }}
        role="img"
        aria-label={title}
      >
        {isLocked && (
          <div className="enrollments-grid-card__lock">
            <Icon src={RiLockLine} size="inline" />
          </div>
        )}
        <div className="enrollments-grid-card__badge">
          <Icon src={BadgeIcon} size="inline" />
          <span>{badgeText}</span>
        </div>
      </div>
      <div className="enrollments-grid-card__details">
        <div className="enrollments-grid-card__title">{title}</div>
      </div>
    </>
  );

  if (isLocked) {
    return (
      <button
        type="button"
        className="enrollments-grid-card enrollments-grid-card--locked"
        aria-label={title}
        onClick={onLockedClick}
      >
        {cardBody}
      </button>
    );
  }

  // Internal SPA routes use React Router's Link so the MFE basename
  // (e.g. /learner-dashboard) is applied; external LMS links use <a>.
  if (to) {
    return (
      <Link to={to} className="enrollments-grid-card" aria-label={title}>
        {cardBody}
      </Link>
    );
  }

  const Tag = homeUrl ? 'a' : 'div';
  const linkProps = homeUrl ? { href: homeUrl } : {};
  return (
    <Tag className="enrollments-grid-card" aria-label={title} {...linkProps}>
      {cardBody}
    </Tag>
  );
};

const buildProgressByCourseId = (summary) => {
  if (!summary) return {};
  const candidates = [];
  if (Array.isArray(summary)) candidates.push(...summary);
  if (Array.isArray(summary?.courses)) candidates.push(...summary.courses);
  if (Array.isArray(summary?.course_summaries)) candidates.push(...summary.course_summaries);
  if (candidates.length === 0 && typeof summary === 'object') {
    const values = Object.values(summary);
    if (values.every((v) => typeof v === 'object' && v !== null)) {
      candidates.push(...values);
    }
  }
  return candidates.reduce((acc, item) => {
    const courseId = item?.course_id || item?.courseId || item?.id || item?.course_key;
    if (!courseId) return acc;
    const percent = item?.complete_percentage
      ?? item?.completion_percentage
      ?? item?.percent_complete
      ?? item?.progress_percent
      ?? item?.progressPercent;
    if (typeof percent === 'number' && !Number.isNaN(percent)) {
      acc[courseId] = percent;
    }
    return acc;
  }, {});
};

const EnrollmentsGrid = () => {
  const intl = useIntl();
  const { authenticatedUser } = useContext(AppContext);
  const userId = authenticatedUser?.userId || authenticatedUser?.id || authenticatedUser?.user_id || null;

  const [toast, setToast] = useState(null);

  const { data: learnerHomeInit } = useQuery({
    queryKey: ['learnerHomeInit'],
    queryFn: fetchLearnerHomeInit,
  });

  const { data: courseSummary } = useQuery({
    queryKey: ['courseSummary', userId],
    queryFn: () => fetchCourseSummary(userId),
    enabled: !!userId,
  });

  const featuresToggled = getFeatureToggle();
  const programsEnabled = featuresToggled.features_programs !== false;
  const { enrolledPrograms } = useEnrolledPrograms(programsEnabled);
  const pathwaysEnabled = featuresToggled.features_learning_pathway === true;
  const { enrolledPathways } = useEnrolledPathways(pathwaysEnabled);

  const { banners } = useFetchBanners();
  const activeBanner = selectDashboardBanner(banners);

  const progressByCourseId = buildProgressByCourseId(courseSummary);

  const initCourses = learnerHomeInit?.courses || [];
  const courseItems = initCourses
    .map((course) => {
      const courseId = course?.courseRun?.courseId;
      const startDate = course?.courseRun?.startDate;
      const isLocked = computeIsLocked(course?.courseRun);
      return {
        id: courseId || course?.course?.courseName,
        title: course?.course?.courseName || intl.formatMessage(messages.fallbackCourseTitle),
        badgeText: intl.formatMessage(messages.courseBadge),
        badgeType: 'course',
        imageUrl: course?.course?.bannerImgSrc
          ? `${getConfig().LMS_BASE_URL}${course.course.bannerImgSrc}`
          : 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=1200&q=60',
        homeUrl: course?.courseRun?.homeUrl || (courseId ? `${getConfig().LMS_BASE_URL}/courses/${courseId}/home` : '#'),
        progressPercent: courseId && progressByCourseId[courseId] != null
          ? Math.round(progressByCourseId[courseId])
          : 0,
        isLocked,
        startDate,
      };
    })
    .filter((item) => !!item.id);

  const handleLockedCourseClick = (item) => {
    const formatted = formatStartDate(item.startDate);
    const days = daysUntil(item.startDate);
    let primary;
    if (formatted && days) {
      primary = intl.formatMessage(messages.courseStartsInDays, { days, date: formatted });
    } else if (formatted) {
      primary = intl.formatMessage(messages.courseStartsOn, { date: formatted });
    } else {
      primary = intl.formatMessage(messages.courseHasNotStarted);
    }
    const message = (
      <>
        <div>{primary}</div>
        {formatted && <div>{intl.formatMessage(messages.addCalendarReminder)}</div>}
      </>
    );
    setToast({ id: Date.now(), message });
  };

  const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=1200&q=60';

  const programItems = enrolledPrograms.map((program) => ({
    id: `program-${program.id}`,
    title: program.name || intl.formatMessage(messages.fallbackProgramTitle),
    badgeText: intl.formatMessage(messages.programBadge),
    badgeType: 'program',
    imageUrl: program.image || FALLBACK_IMAGE,
    to: `/programs/${encodeURIComponent(program.id)}`,
  }));

  const pathwayItems = enrolledPathways.map((pathway) => ({
    id: `pathway-${pathway.id}`,
    title: pathway.name || intl.formatMessage(messages.fallbackPathwayTitle),
    badgeText: intl.formatMessage(messages.pathwayBadge),
    badgeType: 'pathway',
    imageUrl: pathway.image || FALLBACK_IMAGE,
    to: `/pathways/${encodeURIComponent(pathway.id)}`,
  }));

  if (courseItems.length === 0 && programItems.length === 0 && pathwayItems.length === 0) {
    return activeBanner ? <DashboardBanner banner={activeBanner} /> : null;
  }

  return (
    <>
      <div className="enrollments-grid-section">
        <div className="enrollments-grid-section__content">
          {courseItems.length > 0 && (
            <div className="enrollments-grid-section__group">
              <h2 className="enrollments-grid-section__title">
                {intl.formatMessage(messages.enrollmentCoursesTitle)}
              </h2>
              <div className="enrollments-grid-section__grid">
                {courseItems.map((item) => (
                  <EnrollmentCard
                    key={item.id}
                    {...item}
                    onLockedClick={() => handleLockedCourseClick(item)}
                  />
                ))}
              </div>
            </div>
          )}
          {programItems.length > 0 && (
            <div className="enrollments-grid-section__group">
              <h2 className="enrollments-grid-section__title">
                {intl.formatMessage(messages.enrollmentProgramsTitle)}
              </h2>
              <div className="enrollments-grid-section__grid">
                {programItems.map((item) => (
                  <EnrollmentCard key={item.id} {...item} />
                ))}
              </div>
            </div>
          )}
          {pathwayItems.length > 0 && (
            <div className="enrollments-grid-section__group">
              <h2 className="enrollments-grid-section__title">
                {intl.formatMessage(messages.enrollmentPathwaysTitle)}
              </h2>
              <div className="enrollments-grid-section__grid">
                {pathwayItems.map((item) => (
                  <EnrollmentCard key={item.id} {...item} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Toast show={!!toast} variant="warning" onClose={() => setToast(null)}>
        {toast?.message}
      </Toast>
    </>
  );
};

export default EnrollmentsGrid;
